import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, Clock, Users, Building2, Video, RotateCcw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import MeetingNotesPanel from "./MeetingNotesPanel";

/**
 * RiunioneDetail — pagina dettaglio `/app/riunioni/:id` (non-join).
 *
 * Consulta metadata riunione (titolo, data, partecipanti, cliente collegato)
 * e sezione Note inline (riusa MeetingNotesPanel variant="inline") senza
 * entrare nella call Daily. Dalla pagina si può:
 *  - Entrare nella call (scheduled/ongoing) → /join
 *  - Riaprire la riunione (expired) → POST /reopen, poi navigare a /join
 *  - Tornare alla lista riunioni
 */
function fmtDT(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("it-IT", {
        day: "2-digit", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

function StatusChip({ status }) {
    const cfg = {
        ongoing:   { bg: "#DCFCE7", fg: "#166534", label: "In corso" },
        scheduled: { bg: "#DBEAFE", fg: "#1E40AF", label: "Programmata" },
        expired:   { bg: "#FEE2E2", fg: "#7F1D1D", label: "Scaduta" },
    };
    const c = cfg[status] || cfg.scheduled;
    return (
        <span
            data-testid={`riunione-status-${status}`}
            style={{
                display: "inline-flex", alignItems: "center",
                padding: "4px 10px", background: c.bg, color: c.fg,
                borderRadius: 999, fontFamily: '"IBM Plex Mono", monospace',
                fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase",
                fontWeight: 600,
            }}
        >
            {c.label}
        </span>
    );
}

export default function RiunioneDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [meeting, setMeeting] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reopening, setReopening] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/internal/meetings/${id}`);
            setMeeting(data);
        } catch (err) {
            if (err?.response?.status === 404) {
                toast.error("Riunione non trovata");
                navigate("/app/riunioni");
            } else {
                toast.error("Errore caricamento riunione");
            }
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    const handleReopen = async () => {
        setReopening(true);
        try {
            await api.post(`/internal/meetings/${id}/reopen`);
            toast.success("Riunione riaperta");
            await load();
            navigate(`/app/riunioni/${id}/join`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore riapertura");
        } finally {
            setReopening(false);
        }
    };

    const handleJoin = () => navigate(`/app/riunioni/${id}/join`);

    if (loading) return <div style={{ padding: 32, color: "#8A8F88" }}>Caricamento…</div>;
    if (!meeting) return null;

    const isExpired = meeting.status === "expired";
    const canJoin = meeting.status === "ongoing" || meeting.status === "scheduled";

    return (
        <div style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }} data-testid="riunione-detail-page">
            <button
                type="button"
                onClick={() => navigate("/app/riunioni")}
                style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "transparent", border: "none", color: "#5F6560",
                    cursor: "pointer", fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: 11.5, letterSpacing: "0.06em", textTransform: "uppercase",
                    marginBottom: 20, padding: 0,
                }}
                data-testid="riunione-detail-back-btn"
            >
                <ArrowLeft size={12} /> Riunioni
            </button>

            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, marginBottom: 24 }}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                        <h1 style={{ margin: 0, fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: 28, color: "#181B20" }} data-testid="riunione-detail-title">
                            {meeting.title}
                        </h1>
                        <StatusChip status={meeting.status} />
                    </div>
                    {meeting.description && (
                        <div style={{ color: "#5F6560", fontSize: 14, maxWidth: 700 }}>
                            {meeting.description}
                        </div>
                    )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    {canJoin && (
                        <button
                            type="button"
                            className="lt-btn-primary"
                            onClick={handleJoin}
                            data-testid="riunione-detail-join-btn"
                        >
                            <Video size={13} /> Entra nella call
                        </button>
                    )}
                    {isExpired && (
                        <button
                            type="button"
                            className="lt-btn-primary"
                            onClick={handleReopen}
                            disabled={reopening}
                            data-testid="riunione-detail-reopen-btn"
                        >
                            <RotateCcw size={13} /> {reopening ? "Riapro…" : "Riapri riunione"}
                        </button>
                    )}
                </div>
            </div>

            <div
                className="lt-card"
                style={{
                    padding: 20, marginBottom: 20,
                    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 20,
                }}
                data-testid="riunione-detail-info"
            >
                <InfoBlock icon={<Calendar size={14} />} label="Data e ora">
                    {fmtDT(meeting.datetime_start)}
                </InfoBlock>
                <InfoBlock icon={<Clock size={14} />} label="Durata">
                    {meeting.duration_min != null ? `${meeting.duration_min} min` : "—"}
                </InfoBlock>
                <InfoBlock icon={<Building2 size={14} />} label="Cliente">
                    {meeting.client ? (
                        <button
                            type="button"
                            onClick={() => navigate(`/app/clienti/${meeting.client.id}`)}
                            style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", color: "#181B20", textDecoration: "underline", textUnderlineOffset: 2, fontSize: 14 }}
                            data-testid="riunione-detail-client-link"
                        >
                            {meeting.client.company_name}
                        </button>
                    ) : <span style={{ color: "#8A8F88" }}>—</span>}
                </InfoBlock>
                <InfoBlock icon={<Users size={14} />} label={`Partecipanti (${(meeting.participants_internal || []).length})`}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="riunione-detail-participants">
                        {(meeting.participants_internal || []).length === 0 && <span style={{ color: "#8A8F88" }}>—</span>}
                        {(meeting.participants_internal || []).map((p) => (
                            <span key={p.id} style={{ fontSize: 13 }}>{p.name}</span>
                        ))}
                    </div>
                </InfoBlock>
            </div>

            <h2 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 700, fontSize: 18, color: "#181B20", marginBottom: 12 }} data-testid="riunione-detail-notes-title">
                Note della riunione
            </h2>
            <MeetingNotesPanel
                meetingId={id}
                meeting={meeting}
                open={true}
                onClose={() => {}}
                variant="inline"
            />
        </div>
    );
}

function InfoBlock({ icon, label, children }) {
    return (
        <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#8A8F88", fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
                {icon} {label}
            </div>
            <div style={{ color: "#181B20", fontSize: 14, lineHeight: 1.4 }}>{children}</div>
        </div>
    );
}
